import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Lightbulb,
  Loader2,
  Plus,
  RotateCcw,
  Send,
  Share2,
  Trash2,
  WifiOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { MarketingPageShell } from './components/MarketingPageShell';
import {
  MarketingFilterPanel,
  MarketingFilterSearch,
  MarketingFilterSelect,
  marketingFilterSelectClass,
  marketingMatchSummary,
} from './components/MarketingListFilters';
import { ActionIconButton } from './components/ActionIconButton';
import {
  FieldHint,
  RequiredMark,
  clearFieldError,
  fieldClass,
} from '@/client/pages/Admin/shared/form-field';
import { ChipMultiSelect } from '@/client/pages/Admin/shared/ChipMultiSelect';
import { StatusBadge, DeleteConfirmDialog, EmptyState } from './components/marketing-ui';
import { PlatformLogoBadge } from '@/client/pages/Admin/Media/components/PlatformLogo';
import {
  useBulkTransitionPostsMutation,
  useCreateSocialPostMutation,
  useDeleteSocialPostMutation,
  usePostIdeasQuery,
  usePostTemplatesQuery,
  useRetryPostMutation,
  useSocialPostsQuery,
  useSubmitPostMutation,
  useUpdateSocialPostMutation,
} from '@/client/api/marketing/studio';
import { useDestinationsQuery, useProvidersQuery } from '@/client/api/marketing/sources';
import { usePlaysQuery } from '@/client/api/marketing/plays';
import type { ApiSocialPost, ApiSourceAccount } from '@/client/types/marketing/api';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  DISTRIBUTION_STATUS_BADGE,
  DISTRIBUTION_STATUS_LABEL,
  PLATFORM_LABEL,
  type DistributionStatus,
  type SocialPlatform,
} from '@/client/types/marketing';
import {
  MARKETING_PLAY_TAG_LABEL,
  MARKETING_PLAY_TAGS,
  type MarketingPlayTag,
} from '@/client/types/marketing/strategy';

/**
 * Studio → Social, on real data. The publish queue.
 *
 * Three things this page deliberately cannot do:
 *
 * 1. **Set a status.** `status` is excluded from the writable fields server-side,
 *    so a PATCH carrying one is dropped. Movement is submit → approve → (the
 *    beat tick publishes) with retry for failures. The mock had a bulk
 *    "mark as…" that skipped the approver stamp twelve rows at a time.
 *
 * 2. **Publish now.** Approving schedules, or queues for the next tick. There is
 *    no publish endpoint because publishing is the worker's job — a button
 *    promising otherwise would be lying about when the post goes out.
 *
 * 3. **Assume a provider can do everything.** Thread and first-comment fields
 *    are gated on the capability matrix for the destination account's provider,
 *    with the reason shown. Silently dropping a thread and discovering it at
 *    publish time is the failure this avoids.
 */

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

const STATUS_FILTERS: (DistributionStatus | 'all')[] = [
  'all',
  'draft',
  'pending_approval',
  'queued',
  'scheduled',
  'publishing',
  'published',
  'failed',
];

const asTags = (value: unknown): string[] =>
  Array.isArray(value) ? (value as unknown[]).map(String) : [];

const knownPlayTags = (value: unknown): MarketingPlayTag[] =>
  asTags(value).filter((t): t is MarketingPlayTag =>
    (MARKETING_PLAY_TAGS as readonly string[]).includes(t),
  );

const TAG_CHIP_OPTIONS = MARKETING_PLAY_TAGS.map((t) => ({
  value: t,
  label: MARKETING_PLAY_TAG_LABEL[t],
}));

const toDatetimeLocal = (iso?: string | null) => (iso ? iso.slice(0, 16) : '');
const fromDatetimeLocal = (value: string) => (value ? new Date(value).toISOString() : null);

interface FormState {
  saids: number[];
  caption: string;
  threadParts: string[];
  firstComment: string;
  tags: MarketingPlayTag[];
  scheduledAt: string;
  playId: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const emptyForm: FormState = {
  saids: [],
  caption: '',
  threadParts: [],
  firstComment: '',
  tags: [],
  scheduledAt: '',
  playId: '',
};

const toForm = (post: ApiSocialPost): FormState => ({
  saids: post.said ? [post.said] : [],
  caption: post.caption ?? '',
  threadParts: Array.isArray(post.thread_parts)
    ? (post.thread_parts as unknown[]).map(String)
    : [],
  firstComment: post.first_comment ?? '',
  tags: knownPlayTags(post.tags),
  scheduledAt: toDatetimeLocal(post.scheduled_at),
  playId: post.play_id != null ? String(post.play_id) : '',
});

/** Terminal for the operator: nothing they do moves these. */
const isLocked = (status: string) => status === 'published' || status === 'publishing';

const DistributionSocialPage = () => {
  const [statusFilter, setStatusFilter] = useState<DistributionStatus | 'all'>('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ApiSocialPost | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [deleting, setDeleting] = useState<ApiSocialPost | null>(null);
  const [ideasOpen, setIdeasOpen] = useState(false);

  const { data, isLoading, isError, error } = useSocialPostsQuery({ limit: 200 });
  const destinations = useDestinationsQuery();
  const providers = useProvidersQuery();
  const plays = usePlaysQuery({ limit: 100 });
  const ideas = usePostIdeasQuery({ limit: 50 });
  const templates = usePostTemplatesQuery({ limit: 50 });

  const createPost = useCreateSocialPostMutation();
  const updatePost = useUpdateSocialPostMutation();
  const deletePost = useDeleteSocialPostMutation();
  const submitPost = useSubmitPostMutation();
  const retryPost = useRetryPostMutation();
  const bulkTransition = useBulkTransitionPostsMutation();

  const posts: ApiSocialPost[] = data?.items ?? [];
  const playList = plays.data?.items ?? [];

  const accounts: ApiSourceAccount[] = useMemo(
    () => (destinations.data?.accounts ?? []).filter((a) => a.use_as_destination),
    [destinations.data],
  );
  const accountById = (said: number | null) => accounts.find((a) => a.said === said);

  /**
   * Which capabilities the selected destinations all support.
   *
   * Capabilities are per (provider, platform), so a composer targeting three
   * accounts can only offer what every one of them can carry. The reason is
   * shown rather than the control hidden — a missing field reads as a bug,
   * a disabled one with an explanation reads as a constraint.
   */
  const composerCaps = useMemo(() => {
    const chosen = accounts.filter((a) => form.saids.includes(a.said));
    if (chosen.length === 0 || !providers.data) {
      return { thread: true, firstComment: true, reason: '' };
    }
    const supports = (capability: string) =>
      chosen.every((account) => {
        const provider = providers.data!.providers.find((p) =>
          p.platforms.includes(account.platform),
        );
        if (!provider) return true; // Unknown, not unsupported.
        const platforms = provider.capabilities?.[capability];
        if (!platforms) return false;
        return platforms.includes('*') || platforms.includes(account.platform);
      });
    const thread = supports('thread');
    const firstComment = supports('first_comment');
    return {
      thread,
      firstComment,
      reason: 'Not supported by the publishing provider for one of the selected accounts.',
    };
  }, [accounts, form.saids, providers.data]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    posts.forEach((p) => asTags(p.tags).forEach((t) => set.add(t)));
    return [...set].sort();
  }, [posts]);

  const statusFilterOptions = useMemo(
    () =>
      STATUS_FILTERS.map((s) => ({
        value: s,
        label: s === 'all' ? 'All statuses' : DISTRIBUTION_STATUS_LABEL[s],
      })),
    [],
  );

  const tagFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All tags' },
      ...allTags.map((t) => ({ value: t, label: t })),
    ],
    [allTags],
  );

  const filtersActive =
    statusFilter !== 'all' || tagFilter !== 'all' || search.trim().length > 0;

  const clearFilters = () => {
    setStatusFilter('all');
    setTagFilter('all');
    setSearch('');
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return posts
      .filter((p) => statusFilter === 'all' || p.status === statusFilter)
      .filter((p) => tagFilter === 'all' || asTags(p.tags).includes(tagFilter))
      .filter(
        (p) =>
          !q ||
          (p.caption ?? '').toLowerCase().includes(q) ||
          p.handle.toLowerCase().includes(q) ||
          asTags(p.tags).some((t) => t.toLowerCase().includes(q)),
      )
      .sort((a, b) => {
        // Queue order first for things not yet out, then most recent.
        const at = a.scheduled_at ?? a.published_at ?? '';
        const bt = b.scheduled_at ?? b.published_at ?? '';
        if (a.queue_rank !== b.queue_rank && a.status !== 'published') {
          return a.queue_rank - b.queue_rank;
        }
        return bt.localeCompare(at);
      });
  }, [posts, statusFilter, tagFilter, search]);

  useEffect(() => {
    // Drop selections that scrolled out of the current filter, so a bulk action
    // cannot silently act on rows nobody can see.
    setSelected((prev) => {
      const visible = new Set(filtered.map((p) => p.mspid));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  const insights = useMemo(() => {
    const pendingApproval = posts.filter((p) => p.status === 'pending_approval').length;
    const waiting = posts.filter((p) => p.status === 'scheduled' || p.status === 'queued').length;
    const published = posts.filter((p) => p.status === 'published').length;
    const failed = posts.filter((p) => p.status === 'failed').length;
    const unlinked = posts.filter((p) => p.play_id == null).length;
    return { pendingApproval, waiting, published, failed, unlinked };
  }, [posts]);

  const toggleSelected = (mspid: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(mspid)) next.delete(mspid);
      else next.add(mspid);
      return next;
    });

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (post: ApiSocialPost) => {
    setEditing(post);
    setForm(toForm(post));
    setFormErrors({});
    setDialogOpen(true);
  };

  const patchForm = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFormErrors((e) => clearFieldError(e, key));
  };

  const toggleAccount = (said: number) =>
    patchForm(
      'saids',
      form.saids.includes(said) ? form.saids.filter((s) => s !== said) : [...form.saids, said],
    );

  const payloadFor = (account: ApiSourceAccount) => ({
    said: account.said,
    platform: account.platform,
    handle: account.handle,
    caption: form.caption.trim(),
    // Drop what the provider cannot carry rather than sending it to be
    // discarded silently at publish time.
    thread_parts: composerCaps.thread ? form.threadParts.map((t) => t.trim()).filter(Boolean) : [],
    first_comment: composerCaps.firstComment ? form.firstComment.trim() || null : null,
    tags: form.tags,
    scheduled_at: fromDatetimeLocal(form.scheduledAt),
    play_id: form.playId ? Number(form.playId) : null,
  });

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FormErrors = {};
    if (!form.caption.trim()) {
      errors.caption = 'Caption is required';
    }
    if (form.saids.length === 0) {
      errors.saids = 'Pick at least one destination account';
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error('Fix the highlighted fields before saving');
      return;
    }

    const chosen = accounts.filter((a) => form.saids.includes(a.said));
    try {
      if (editing) {
        await updatePost.mutateAsync({ mspid: editing.mspid, ...payloadFor(chosen[0]) });
        toast.success('Post updated');
      } else {
        // One row per destination: each publishes independently, and one
        // platform failing must not take the others down with it.
        await Promise.all(chosen.map((account) => createPost.mutateAsync(payloadFor(account))));
        toast.success(
          chosen.length === 1
            ? `Drafted to ${chosen[0].display_name}`
            : `Drafted to ${chosen.length} accounts`,
        );
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not save the post'));
    }
  };

  const runBulk = (action: 'submit' | 'approve' | 'retry') => {
    const ids = [...selected];
    if (ids.length === 0) return;
    bulkTransition.mutate(
      { ids, action },
      {
        onSuccess: (res) => {
          const updated = res?.data?.updated?.length ?? 0;
          const failed = res?.data?.failed ?? [];
          // Partial success is the normal case — a post already past the gate
          // cannot be submitted again. Report both halves.
          if (failed.length === 0) toast.success(`${updated} post(s) ${action}ed`);
          else if (updated === 0) toast.error(`None could be ${action}ed (${failed.length} refused)`);
          else toast.warning(`${updated} ${action}ed, ${failed.length} refused`);
          setSelected(new Set());
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update the posts')),
      },
    );
  };

  const confirmDelete = () => {
    if (!deleting) return;
    deletePost.mutate(
      { mspid: deleting.mspid },
      {
        onSuccess: () => toast.success('Post removed'),
        onError: (err) => toast.error(getApiErrorMessage(err, 'Could not remove the post')),
      },
    );
    setDeleting(null);
  };

  return (
    <MarketingPageShell
      title="Social"
      description="The publish queue. Approving schedules a post — the worker publishes it on the next tick, so nothing here goes out the instant you click."
      crumbs={[
        { label: 'Marketing', href: '/admin/marketing' },
        { label: 'Studio' },
        { label: 'Social' },
      ]}
      icon={Share2}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            onClick={() => setIdeasOpen((v) => !v)}
          >
            <Lightbulb className="h-4 w-4" /> Ideas
          </Button>
          <Button type="button" className="gap-2" onClick={openAdd}>
            <Plus className="h-4 w-4" /> New post
          </Button>
        </div>
      }
      stats={[
        { key: 'pending', label: 'Pending approval', value: insights.pendingApproval },
        { key: 'waiting', label: 'Queued / scheduled', value: insights.waiting },
        { key: 'published', label: 'Published', value: insights.published },
        { key: 'failed', label: 'Failed', value: insights.failed },
      ]}
    >
      {insights.failed > 0 && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">
              {insights.failed} post{insights.failed === 1 ? '' : 's'} failed to publish
            </p>
            <button
              type="button"
              onClick={() => setStatusFilter('failed')}
              className="text-xs underline underline-offset-2"
            >
              Show them — each carries the provider’s own reason
            </button>
          </div>
        </div>
      )}

      {ideasOpen && (
        <div className="mb-4 grid gap-3 rounded-lg border border-gray-200 bg-white p-3 lg:grid-cols-2">
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Ideas
            </p>
            {(ideas.data?.items ?? []).length === 0 ? (
              <p className="text-xs text-gray-400">
                No ideas captured yet. Anything worth writing down goes here.
              </p>
            ) : (
              <ul className="space-y-1">
                {(ideas.data?.items ?? []).map((idea) => (
                  <li key={idea.piid}>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(null);
                        setForm({ ...emptyForm, caption: idea.body, tags: knownPlayTags(idea.tags) });
                        setIdeasOpen(false);
                        setDialogOpen(true);
                      }}
                      className="w-full truncate rounded-md border border-gray-100 px-2 py-1.5 text-left text-xs hover:bg-gray-50"
                    >
                      {idea.body}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              Templates
            </p>
            {(templates.data?.items ?? []).length === 0 ? (
              <p className="text-xs text-gray-400">No templates yet.</p>
            ) : (
              <ul className="space-y-1">
                {(templates.data?.items ?? []).map((t) => (
                  <li key={t.ptid}>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(null);
                        setForm({ ...emptyForm, caption: t.body });
                        setIdeasOpen(false);
                        setDialogOpen(true);
                      }}
                      className="w-full truncate rounded-md border border-gray-100 px-2 py-1.5 text-left text-xs hover:bg-gray-50"
                    >
                      <span className="font-medium">{t.name}</span> — {t.body}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <MarketingFilterPanel
        summary={marketingMatchSummary(filtered.length, posts.length, 'post', isLoading)}
        showClear={filtersActive}
        onClear={clearFilters}
      >
        <MarketingFilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Search caption, handle, tag…"
        />
        <MarketingFilterSelect
          options={statusFilterOptions}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as DistributionStatus | 'all')}
          placeholder="All statuses"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
        <MarketingFilterSelect
          options={tagFilterOptions}
          value={tagFilter}
          onChange={setTagFilter}
          placeholder="All tags"
          clearValue="all"
          className={marketingFilterSelectClass}
        />
      </MarketingFilterPanel>

      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
          <span className="text-sm font-medium text-blue-900">{selected.size} selected</span>
          {/* Transitions only. There is no bulk "mark as" — that is the same
              approver bypass, twelve rows at a time. */}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1"
            disabled={bulkTransition.isPending}
            onClick={() => runBulk('submit')}
          >
            <Send className="h-3.5 w-3.5" /> Submit
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1"
            disabled={bulkTransition.isPending}
            onClick={() => runBulk('approve')}
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Approve
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 gap-1"
            disabled={bulkTransition.isPending}
            onClick={() => runBulk('retry')}
          >
            <RotateCcw className="h-3.5 w-3.5" /> Retry
          </Button>
          <button
            type="button"
            onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-blue-800 underline underline-offset-2"
          >
            Clear
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="rounded-xl border border-gray-100 bg-white px-6 py-16 text-center text-sm text-gray-500 shadow-sm">
          <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
          Loading the publish queue…
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-amber-100 bg-amber-50/40 px-6 py-16 text-center text-sm shadow-sm">
          <WifiOff className="mb-2 inline h-5 w-5 text-amber-600" />
          <div className="font-medium text-amber-800">Could not load the publish queue</div>
          <div className="mt-1 text-xs text-gray-500">
            {getApiErrorMessage(error, 'The marketing API did not respond')}
          </div>
        </div>
      ) : posts.length === 0 ? (
        <EmptyState label="Nothing queued yet — write a post, or queue variants from a content item." />
      ) : filtered.length === 0 ? (
        <EmptyState label="No posts match these filters." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <TooltipProvider delayDuration={200}>
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="w-8 px-3 py-2.5" />
                <th className="px-3 py-2.5 font-medium">Post</th>
                <th className="px-3 py-2.5 font-medium">Destination</th>
                <th className="px-3 py-2.5 font-medium">Play</th>
                <th className="px-3 py-2.5 font-medium">Goes out</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((post) => {
                const account = accountById(post.said);
                const locked = isLocked(post.status);
                return (
                  <tr key={post.mspid} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300"
                        checked={selected.has(post.mspid)}
                        onChange={() => toggleSelected(post.mspid)}
                        aria-label="Select post"
                      />
                    </td>
                    <td className="max-w-[22rem] px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => !locked && openEdit(post)}
                        disabled={locked}
                        className={cn(
                          'line-clamp-2 text-left font-medium text-gray-900',
                          !locked && 'underline-offset-2 hover:underline',
                        )}
                      >
                        {post.caption ?? '(no caption)'}
                      </button>
                      {post.status === 'failed' && post.fail_reason && (
                        <p className="mt-1 line-clamp-2 text-[11px] text-rose-700">
                          {post.fail_reason}
                          {post.attempt_count > 1 ? ` (attempt ${post.attempt_count})` : ''}
                        </p>
                      )}
                      {post.external_url && (
                        <a
                          href={post.external_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[11px] text-blue-700 hover:underline"
                        >
                          View live post →
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <PlatformLogoBadge
                          platform={post.platform as SocialPlatform}
                          size="sm"
                        />
                        <div className="min-w-0">
                          <p className="truncate">
                            {account?.display_name ?? PLATFORM_LABEL[
                              post.platform as SocialPlatform
                            ] ?? post.platform}
                          </p>
                          <p className="truncate text-gray-400">{post.handle}</p>
                        </div>
                      </div>
                    </td>
                    <td className="max-w-[8rem] px-3 py-2.5 text-xs text-gray-600">
                      {playList.find((p) => p.play_id === post.play_id)?.name ?? (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-600">
                      {post.published_at ? (
                        new Date(post.published_at).toLocaleString()
                      ) : post.scheduled_at ? (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3 text-gray-400" />
                          {new Date(post.scheduled_at).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-gray-400">next tick</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusBadge
                        label={DISTRIBUTION_STATUS_LABEL[post.status] ?? post.status}
                        className={DISTRIBUTION_STATUS_BADGE[post.status] ?? ''}
                      />
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="inline-flex items-center gap-1">
                        {post.status === 'draft' && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1"
                            onClick={() =>
                              submitPost.mutate(
                                { mspid: post.mspid },
                                {
                                  onSuccess: () => toast.success('Submitted for approval'),
                                  onError: (err) =>
                                    toast.error(getApiErrorMessage(err, 'Could not submit')),
                                },
                              )
                            }
                          >
                            <Send className="h-3.5 w-3.5" /> Submit
                          </Button>
                        )}
                        {post.status === 'failed' && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1"
                            onClick={() =>
                              retryPost.mutate(
                                { mspid: post.mspid },
                                {
                                  onSuccess: () => toast.success('Requeued'),
                                  onError: (err) =>
                                    toast.error(getApiErrorMessage(err, 'Could not retry')),
                                },
                              )
                            }
                          >
                            <RotateCcw className="h-3.5 w-3.5" /> Retry
                          </Button>
                        )}
                        {post.status === 'pending_approval' && (
                          <Link
                            to="/admin/marketing/crew/approvals"
                            className="text-xs text-blue-700 hover:underline"
                          >
                            Approvals →
                          </Link>
                        )}
                        <ActionIconButton
                          label={
                            locked
                              ? 'A published post cannot be removed from here — it exists on the platform'
                              : 'Delete'
                          }
                          disabled={locked}
                          onClick={() => setDeleting(post)}
                          className="text-gray-400 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </ActionIconButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </TooltipProvider>
          <div className="border-t border-gray-100 bg-gray-50 px-4 py-2 text-xs text-gray-500">
            {filtered.length} shown. Approving does not publish — a worker claims scheduled posts
            every 60 seconds and publishes them then.
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit post' : 'New post'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Edits apply to this post only. Status moves through Submit and Approvals.'
                : 'Each destination gets its own post, so one platform failing cannot take the others down.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={submitForm} noValidate className="space-y-4">
            <div className="space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                Destinations <RequiredMark />
              </span>
              {destinations.isLoading && (
                <p className="text-xs text-gray-400">Loading accounts…</p>
              )}
              {!destinations.isLoading && accounts.length === 0 && (
                <p className="text-xs text-amber-700">
                  No accounts are marked as destinations. Set one under Sources → Accounts.
                </p>
              )}
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-gray-100 p-2">
                {accounts.map((a) => (
                  <label
                    key={a.said}
                    className={cn(
                      'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                      form.saids.includes(a.said) ? 'bg-blue-50' : 'hover:bg-gray-50',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300"
                      checked={form.saids.includes(a.said)}
                      onChange={() => toggleAccount(a.said)}
                      disabled={!!editing && form.saids.length > 0 && !form.saids.includes(a.said)}
                    />
                    <PlatformLogoBadge platform={a.platform as SocialPlatform} size="sm" />
                    <span className="min-w-0 flex-1 truncate">
                      {a.display_name} <span className="text-gray-400">{a.handle}</span>
                    </span>
                  </label>
                ))}
              </div>
              <FieldHint error={formErrors.saids} />
            </div>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                Caption <RequiredMark />
              </span>
              <textarea
                value={form.caption}
                onChange={(e) => patchForm('caption', e.target.value)}
                className={fieldClass(
                  'min-h-[96px] w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2',
                  formErrors.caption,
                )}
                aria-invalid={!!formErrors.caption}
              />
              <FieldHint error={formErrors.caption} />
            </label>

            <label className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                First comment{' '}
                {!composerCaps.firstComment && (
                  <span className="font-normal text-amber-700">— not available</span>
                )}
              </span>
              <input
                value={form.firstComment}
                onChange={(e) => setForm((f) => ({ ...f, firstComment: e.target.value }))}
                className={inputClass}
                disabled={!composerCaps.firstComment}
                placeholder={composerCaps.firstComment ? 'Hashtags, link, credits…' : ''}
              />
              {!composerCaps.firstComment && (
                <span className="text-[11px] text-amber-700">{composerCaps.reason}</span>
              )}
            </label>

            <div className="space-y-1.5">
              <span className="text-xs font-medium text-gray-600">
                Thread{' '}
                {!composerCaps.thread && (
                  <span className="font-normal text-amber-700">— not available</span>
                )}
              </span>
              {composerCaps.thread ? (
                <>
                  {form.threadParts.map((part, i) => (
                    <input
                      key={i}
                      value={part}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          threadParts: f.threadParts.map((p, j) => (j === i ? e.target.value : p)),
                        }))
                      }
                      className={inputClass}
                      placeholder={`Part ${i + 2}`}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, threadParts: [...f.threadParts, ''] }))}
                    className="text-xs text-blue-700 hover:underline"
                  >
                    + Add thread part
                  </button>
                </>
              ) : (
                <p className="text-[11px] text-amber-700">{composerCaps.reason}</p>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Scheduled for</span>
                <input
                  type="datetime-local"
                  value={form.scheduledAt}
                  onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))}
                  className={inputClass}
                />
                <span className="text-[11px] text-gray-400">
                  Leave empty to go out on the next tick after approval.
                </span>
              </label>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-gray-600">Play</span>
                <select
                  value={form.playId}
                  onChange={(e) => setForm((f) => ({ ...f, playId: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">No play linked</option>
                  {playList.map((p) => (
                    <option key={p.play_id} value={String(p.play_id)}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="block space-y-1.5">
              <span className="text-xs font-medium text-gray-600">Tags</span>
              <ChipMultiSelect
                options={TAG_CHIP_OPTIONS}
                value={form.tags}
                onChange={(next) => patchForm('tags', next)}
                aria-label="Post tags"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={createPost.isPending || updatePost.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createPost.isPending || updatePost.isPending}>
                {createPost.isPending || updatePost.isPending
                  ? 'Saving…'
                  : editing
                    ? 'Save changes'
                    : 'Save as draft'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete post?"
        description={
          deleting
            ? `This removes the queued post. Anything already published stays on the platform.`
            : ''
        }
        onConfirm={confirmDelete}
      />
    </MarketingPageShell>
  );
};

export default DistributionSocialPage;
